<template>
  <modal
    size="lg"
    ref="modalComponent"
    :show="show"
    @hidden="$emit('input', false)"
    :scrollable="false"
    :hide-footer="true"
    :title="`Close this ${entityMap.name}`"
  >
    <highlighted-card class="mb-4">
      <div class="d-flex">
        <div class="d-flex flex-grow-1">
          <i class="flex-shrink-0" :class="entityMap.icon"></i>
          <div class="title px-2 bold text-medium-1" :title="entity.title">{{ entity.title }}</div>
        </div>
        <progress-indicator :completed="completedPercent" :variant="colorVariant" />
      </div>
    </highlighted-card>
    <div class="mb-4">
      <label class="text-normal-1">Choose a {{ entityMap.name }} outcome</label>
      <div class="pill-group mx-n1">
        <selectable-pill
          :key="outcome.id"
          @click="selectOutcome(outcome.id)"
          :variant="outcome.variant"
          :selected="formData.outcome === outcome.id"
          v-for="outcome in OUTCOMES"
          >{{ outcome.label }}
        </selectable-pill>
      </div>
      <div class="field-error-message" v-if="getFieldError('outcome')">
        {{ getFieldError('outcome') }}
      </div>
    </div>
    <div class="mb-4">
      <label class="text-normal-1">Add a closing note</label>
      <ck-editor
        class="editor mx-n1"
        :class="{ 'errored-field': !!getFieldError('comment') }"
        :config="editorConfig"
        :id="'editor-' + uuid"
        @change="onCommentChange"
        @save="onSubmit"
      />
      <div class="field-error-message" v-if="getFieldError('comment')">
        {{ getFieldError('comment') }}
      </div>
    </div>
    <submit-buttons
      class="submit-buttons"
      :is-loading="isSubmitting"
      submit-text="Close"
      @save="onSubmit"
      @cancel="hideModal"
    >
      <a
        class="text-secondary"
        href="https://thenorth.io/templates/close-and-score-okrs/"
        target="_blank"
        rel="nofollow noopener noreferrer"
        >Examples</a
      >
    </submit-buttons>
  </modal>
</template>

<script>
import { getCKEditorConfig } from '@/ckeditorConfig'
import CkEditor from '@/components/CkEditor'
import HighlightedCard from '@/components/HighlightedCard'
import Modal from '@/components/Modal'
import ProgressIndicator from '@/components/ProgressIndicator'
import SelectablePill from '@/components/SelectablePill'
import SubmitButtons from '@/components/SubmitButtons'
import { OUTCOMES, VARIANT_MAP } from '@/constants/common'
import { ENTITY_MAP, ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import { calculateCompletionPercent } from '@/utils/calculateCompletionPercent'
import getGoalStatus from '@/utils/getGoalStatus'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'

/**
 This component gives capability to close Goal/KR. Goal/KR object can be passed as prop - entity
 @emits
 - closed
 @eventbus emits
 - KR_CLOSED
 - GOAL_CLOSED
 */
export default {
  name: 'CloseEntity',
  props: ['show', 'entity'],
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    SubmitButtons,
    SelectablePill,
    CkEditor,
    HighlightedCard,
    ProgressIndicator
  },
  data() {
    return {
      isSubmitting: false,
      formData: {
        outcome: '',
        comment: ''
      },
      OUTCOMES,
      errors: null
    }
  },
  mixins: [MembershipsMixin, EventBusMixin, ComponentIdMixin],
  computed: {
    editorConfig() {
      return getCKEditorConfig({
        users: this.usersInScope,
        height: 215,
        orgId: this.entity.organizationId,
        placeholder:
          'Write a summary of the outcome • what went well • what can be improved • and any action items'
      })
    },
    usersInScope() {
      return this.getUsersInScope(this.entity.teamId, this.entity.organizationId)
    },
    entityType() {
      return this.entity.goalId ? ENTITY_TYPE.KEY_RESULT : ENTITY_TYPE.GOAL
    },
    entityMap() {
      return ENTITY_MAP[this.entityType]
    },
    completedPercent() {
      if (this.entityType === ENTITY_TYPE.GOAL) return Math.round(this.entity.progress * 100)
      else if (this.entityType === ENTITY_TYPE.KEY_RESULT) {
        const { current, start, end } = this.entity
        return calculateCompletionPercent(start, end, current)
      }
      return null
    },
    entityStatus() {
      if (this.entityType === ENTITY_TYPE.GOAL) return getGoalStatus(this.entity)
      else if (this.entityType === ENTITY_TYPE.KEY_RESULT) return this.entity.status
      return null
    },
    colorVariant() {
      return VARIANT_MAP['status-' + this.entityStatus]
    }
  },
  methods: {
    hideModal() {
      this.$refs.modalComponent.hide()
    },
    selectOutcome(outcome) {
      this.clearError('outcome')
      this.formData = {
        ...this.formData,
        outcome
      }
    },
    onCommentChange(comment) {
      this.clearError('comment')
      this.formData = {
        ...this.formData,
        comment
      }
    },
    async onSubmit() {
      this.isSubmitting = true
      this.errors = null
      const payload = {
        action: 'close',
        ...this.formData
      }
      const orgId = this.entity.organizationId
      const goalId = this.entityType === ENTITY_TYPE.GOAL ? this.entity.id : this.entity.goalId
      const url =
        this.entityType === ENTITY_TYPE.GOAL
          ? urlManager.goalDetail({ orgId, goalId })
          : urlManager.GoalKRDetail({ orgId, goalId, krId: this.entity.id })
      try {
        await service.client.post(url, payload)
        this.$store.dispatch('getGoal', { orgId, goalId })
        const eventToEmit =
          this.entityType === ENTITY_TYPE.GOAL ? events.GOAL_CLOSED : events.KR_CLOSED
        this.$emit('closed')
        this.eventBusEmit(eventToEmit)
        this.hideModal()
      } catch (e) {
        const errorDetails = e.response.data?.detail
        if (!errorDetails) {
          showAPIError(e)
        } else {
          this.errors = errorDetails
        }
      }
      this.isSubmitting = false
    },
    getFieldError(field) {
      return R.path([field, '0'], this.errors)
    },
    clearError(field) {
      this.errors = R.omit([field], this.errors)
    }
  },
  async mounted() {}
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/errors';

.pill-group {
  display: flex;
  gap: 10px;
}

.editor {
  border-radius: 10px;
  border: 1px solid $borderTertiary;
  overflow: hidden;
  padding: 0 4px 4px 4px;
}
.title {
  @include lineClamp(2);
}
i {
  margin-top: 4px;
  &.kr-icon-greyed {
    @include dimension(12px, 15px);
  }
}
</style>
